// Pages-CMS API surface (M4 — motiflink §2.3/§2.4).
//
// Reshaped from the pre-M4 client: blocks now carry
// `{content, settings, translations, title, isActive}` instead of
// `{data, status}`, and pages carry
// `{isActive, isHomepage, title, metaTitle, metaDescription, translations}`
// instead of `{status, publishedAt, seoTitleKey, seoDescriptionKey}`.
//
// There is NO publish endpoint any more. Marketing is SSR, so an edit is live
// the moment it is saved; `isActive` hides a page or block, and snapshots
// (page_blocks_backup) are the undo.

import { api } from './api';

/** A per-locale overlay: `{ en: { title: 'Hello', … } }`. `content` is Arabic. */
export type TranslationOverlay = Record<string, Record<string, unknown>>;

export type BlockContent = Record<string, unknown>;

export interface PageSummary {
  id: string;
  slug: string;
  title: string | null;
  isActive: boolean;
  isHomepage: boolean;
  updatedAt: string;
  blockCount: number;
}

export interface PageDetail {
  id: string;
  slug: string;
  title: string | null;
  metaTitle: string | null;
  metaDescription: string | null;
  isActive: boolean;
  isHomepage: boolean;
  /** Overlay for the page's own copy — `{ en: { title, metaTitle, … } }`. */
  translations: TranslationOverlay;
  updatedAt: string;
}

export interface BlockSummary {
  id: string;
  type: string;
  /** Admin-only label shown in the arrange list. Never rendered on the site. */
  title: string | null;
  sortOrder: number;
  isActive: boolean;
  content: BlockContent;
  settings: Record<string, unknown>;
  translations: TranslationOverlay;
  updatedAt: string;
}

export interface Snapshot {
  id: string;
  label: string | null;
  blockCount: number;
  createdAt: string;
  createdBy: string | null;
}

// ─── Pages ──────────────────────────────────────────────────────────────────

export async function listPages(signal?: AbortSignal): Promise<PageSummary[]> {
  const { data } = await api<{ data: PageSummary[] }>('/admin/pages', { signal });
  return data;
}

export async function getPage(
  id: string,
  signal?: AbortSignal,
): Promise<{ page: PageDetail; blocks: BlockSummary[] }> {
  const { data } = await api<{ data: { page: PageDetail; blocks: BlockSummary[] } }>(
    `/admin/pages/${id}`,
    { signal },
  );
  return data;
}

export interface PageInput {
  slug: string;
  title?: string | null;
  metaTitle?: string | null;
  metaDescription?: string | null;
  isActive?: boolean;
  isHomepage?: boolean;
  translations?: TranslationOverlay;
}

export async function createPage(input: PageInput): Promise<{ id: string }> {
  const { data } = await api<{ data: { id: string } }>('/admin/pages', {
    method: 'POST',
    body: input,
  });
  return data;
}

export async function updatePage(id: string, input: Partial<PageInput>): Promise<void> {
  await api(`/admin/pages/${id}`, { method: 'PATCH', body: input });
}

export async function deletePage(id: string): Promise<void> {
  await api(`/admin/pages/${id}`, { method: 'DELETE' });
}

// ─── Blocks ─────────────────────────────────────────────────────────────────

export interface BlockInput {
  type: string;
  title?: string | null;
  content?: BlockContent;
  settings?: Record<string, unknown>;
  translations?: TranslationOverlay;
  isActive?: boolean;
  sortOrder?: number;
}

export async function createBlock(
  pageId: string,
  input: BlockInput,
): Promise<{ id: string; sortOrder: number }> {
  const { data } = await api<{ data: { id: string; sortOrder: number } }>(
    `/admin/pages/${pageId}/blocks`,
    { method: 'POST', body: input },
  );
  return data;
}

export async function updateBlock(
  pageId: string,
  blockId: string,
  input: Partial<Omit<BlockInput, 'type'>>,
): Promise<void> {
  await api(`/admin/pages/${pageId}/blocks/${blockId}`, { method: 'PATCH', body: input });
}

export async function deleteBlock(pageId: string, blockId: string): Promise<void> {
  await api(`/admin/pages/${pageId}/blocks/${blockId}`, { method: 'DELETE' });
}

/**
 * Full reindex: the caller sends every block id in its new order and the API
 * writes `sort_order = 0..n-1`. Sending the whole list (rather than a
 * from/to pair) means a stale client can't interleave two moves into a
 * duplicate ordering.
 */
export async function reorderBlocks(pageId: string, blockIds: string[]): Promise<void> {
  await api(`/admin/pages/${pageId}/blocks/reorder`, {
    method: 'PATCH',
    // The API takes explicit {id, orderIndex} pairs, so position is stated
    // rather than inferred from array index.
    body: { order: blockIds.map((id, orderIndex) => ({ id, orderIndex })) },
  });
}

// ─── Snapshots (the undo, in place of draft/publish) ────────────────────────

/** Wire shape of a snapshot row, keyed by snapshot_id (one row per snapshot). */
interface SnapshotWire {
  snapshotId: string;
  label: string;
  snapshottedAt: string;
  snapshottedBy: string | null;
  blockCount: number;
}

export async function listSnapshots(pageId: string, signal?: AbortSignal): Promise<Snapshot[]> {
  const { data } = await api<{ data: SnapshotWire[] }>(`/admin/pages/${pageId}/snapshots`, {
    signal,
  });
  return data.map((s) => ({
    id: s.snapshotId,
    label: s.label,
    blockCount: s.blockCount,
    createdAt: s.snapshottedAt,
    createdBy: s.snapshottedBy,
  }));
}

export async function createSnapshot(
  pageId: string,
  label?: string | null,
): Promise<{ snapshotId: string; label: string }> {
  const { data } = await api<{ data: { snapshotId: string; label: string } }>(
    `/admin/pages/${pageId}/snapshots`,
    {
      method: 'POST',
      // Omit rather than send null: the API defaults an absent label, but a
      // null one fails validation.
      body: label ? { label } : {},
    },
  );
  return data;
}

/**
 * Restore takes a snapshot of the CURRENT state first (motiflink §2.4), so a
 * restore is itself reversible one step. That happens server-side.
 */
export async function restoreSnapshot(pageId: string, snapshotId: string): Promise<void> {
  await api(`/admin/pages/${pageId}/snapshots/${snapshotId}/restore`, { method: 'POST' });
}

export async function deleteSnapshot(pageId: string, snapshotId: string): Promise<void> {
  await api(`/admin/pages/${pageId}/snapshots/${snapshotId}`, { method: 'DELETE' });
}
